/**
 * Small pieces the people screens share: why a submission belongs to its
 * person, a person in one line, the stream chips, and the Undo that follows
 * every merge and unlink.
 */
import { Badge, Button, Group, Stack, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { PersonBrief, PersonStream } from '../../api/people';
import { FONTS, TOKENS } from '../../theme';
import { relativeTime } from '../../ui/format';
import type { Vocab } from '../../vocab';

/** Why a submission is this person's, in a word or two. */
export function linkedByLabel(linkedBy: string): { label: string; detail: string; colour: string } {
  switch (linkedBy) {
    case 'first': return { label: 'First', detail: 'The earliest of them', colour: 'gray' };
    case 'email': return { label: 'Same email', detail: 'Shares an email address with an earlier one', colour: 'chartBlue' };
    case 'phone': return { label: 'Same phone', detail: 'Shares a phone number with an earlier one', colour: 'chartViolet' };
    case 'merge': return { label: 'Merged', detail: 'Brought here by a merge', colour: 'chartAmber' };
    case 'moved': return { label: 'Unlinked here', detail: 'Taken out of another person', colour: 'chartCyan' };
    default: return { label: linkedBy || 'Linked', detail: '', colour: 'gray' };
  }
}

export function LinkedByChip({ linkedBy }: { linkedBy: string }) {
  const { label, detail, colour } = linkedByLabel(linkedBy);
  const chip = (
    <Badge size="sm" variant="light" color={colour} radius="sm" tt="none" data-testid="linked-by">
      {label}
    </Badge>
  );
  return detail ? <Tooltip label={detail} withArrow openDelay={300}>{chip}</Tooltip> : chip;
}

/** Which rule a suggestion matched on. */
export function ruleLabel(rule: string): string {
  if (rule === 'email') return 'Same email';
  if (rule === 'phone') return 'Same phone';
  if (rule === 'email+phone') return 'Same email and phone';
  return rule;
}

export function StreamChips({ streams, max = 3 }: { streams: PersonStream[]; max?: number }) {
  if (streams.length === 0) return null;
  const shown = streams.slice(0, max);
  const more = streams.length - shown.length;
  return (
    <Group gap={4} wrap="wrap">
      {shown.map((s) => (
        <Badge key={s.id} size="sm" variant="default" radius="sm" tt="none" fw={500} maw={180}>
          {s.name}
        </Badge>
      ))}
      {more > 0 && <Text size="xs" c="dimmed">{`+${more}`}</Text>}
    </Group>
  );
}

/** A person's display name, falling back to what identifies them. */
export function personName(p: { name: string; email: string; phone: string }): string {
  return p.name.trim() || p.email || p.phone || '(no name)';
}

/** One person in a few lines, for suggestions and the merge confirmation. */
export function PersonBriefView({ person, v }: { person: PersonBrief; v: Vocab }) {
  return (
    <Stack gap={2} style={{ minWidth: 0 }}>
      <Text size="sm" fw={600} truncate="end">{personName(person)}</Text>
      {person.email && <Text size="xs" c="dimmed" truncate="end">{person.email}</Text>}
      {person.phone && <Text size="xs" c="dimmed" style={{ fontFamily: FONTS.mono }}>{person.phone}</Text>}
      <Text size="xs" style={{ color: TOKENS.textSecondary }}>
        {`${v.count(person.submissions, 'lead')} · last ${relativeTime(person.last_seen)}`}
      </Text>
    </Stack>
  );
}

/** A figure with its label, as the company page draws them. */
export function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.06em', fontFamily: FONTS.mono }}>
        {label}
      </Text>
      <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: TOKENS.text, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
    </div>
  );
}

/**
 * Say a merge or unlink is done, with an Undo that stays a while. `undo`
 * runs the undo and says what happened; it is the caller's mutation, so the
 * caches it touches are invalidated as for any other write.
 */
export function notifyWithUndo({
  title, message, undo,
}: { title: string; message: string; undo: () => Promise<void> }) {
  const id = notifications.show({
    color: 'teal',
    title,
    autoClose: 10000,
    message: (
      <Group justify="space-between" gap="sm" wrap="nowrap">
        <Text size="sm">{message}</Text>
        <Button
          size="compact-xs"
          variant="light"
          onClick={async () => {
            notifications.hide(id);
            await undo();
          }}
        >
          Undo
        </Button>
      </Group>
    ),
  });
  return id;
}

/** The three ways submissions can be linked, for the organisation and for one stream. */
export function linkModeOptions(v: Vocab) {
  return [
    { value: 'off', label: 'Do not link' },
    { value: 'suggest', label: 'Suggest, for someone to check' },
    { value: 'auto', label: `Link into one ${v.one('person')} automatically` },
  ];
}
